import { useEffect, useRef, useState, type RefObject } from "react";
import { ArrowDown, ChevronDown } from "lucide-react";
import {
  LIST_CUE_THRESHOLD_PX,
  hasMoreBelow,
  hiddenBelow,
  scrollStep,
} from "@/lib/scrollCue";
import { TOUCH_SQUARE } from "@/components/buttonStyles";

// The "more below" cue (BRK-S240, BRK-S241): a down arrow that shows while
// content continues past the visible area and hides at the end, so a long form
// or list never looks finished when it isn't. Three placements:
//
// - PageScrollCue ends <main>. A zero-height sticky anchor floats 1rem above
//   the viewport's bottom, lifted over the sticky footer at lg+ (BRK-S228) and
//   over a bottom dock such as a list's pager (--brk-dock-h, BRK-S211), so
//   the CSS owns the geometry: while the anchor is stuck, the gap between it
//   and the end of the page's content is exactly what is hidden (unstuck, it
//   rests below that end, so the gap is nil). There is no breakpoint logic
//   here to drift from index.css. A page that keeps its own cue
//   (`data-own-scroll-cue`: the wizard, BRK-S359) has none.
// - ListScrollCue overlays the bottom edge of a scroll box (the sidebar's
//   destination list, BRK-S229) and reads the box's own scroll metrics.
// - DockScrollCue sits in a bottom dock that stays on screen while the page
//   scrolls (the wizard's Back and Next, BRK-S359), between its buttons, and
//   reads how far the content runs on under the dock. It is a plain arrow, as
//   quiet as the dock's buttons, since a button floating over a form covered
//   the field under it (Nav, 2026-10-14: "our arrow is conflicting with the
//   design").
//
// It is a pointer convenience, not a control: aria-hidden and out of the tab
// order. Keyboard and screen-reader users scroll natively (and the lg+
// scroll-padding keeps a focused field clear of the footer), and a focusable
// button that vanishes at the end would strand focus. It never takes focus on a
// click either, so a broker mid-edit keeps their cursor in the field.

/** Smooth, unless the broker asked the OS for less motion. */
function scrollBehavior(): ScrollBehavior {
  const reduce =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return reduce ? "auto" : "smooth";
}

/** Calls `update` whenever one of `boxes` resizes. jsdom has no
 *  ResizeObserver; there the scroll and resize listeners still apply. */
function observeResize(boxes: Element[], update: () => void): () => void {
  if (typeof ResizeObserver === "undefined") return () => {};
  const observer = new ResizeObserver(update);
  boxes.forEach((box) => observer.observe(box));
  return () => observer.disconnect();
}

const px = (value: string) => Number.parseFloat(value) || 0;

const OWN_CUE = "[data-own-scroll-cue]";

/** Whether a page keeps its own cue: the wizard's is in Back and Next's bar
 *  (BRK-S359), and the page's would only float over the form. */
function keepsOwnCue(page: Element): boolean {
  return page.matches(OWN_CUE) || page.querySelector(OWN_CUE) !== null;
}

/** Viewport y where a page's content ends. A self-padded page (the Exchange
 *  landing, BaseForm) ends in its own bottom padding, which is whitespace, not
 *  "more below", so it doesn't count. A wrapper with one child (the shell's
 *  reading-width column) defers to the page inside it. A list's pager at the
 *  page's end is a dock the cue floats over, not content, so the content ends
 *  where the pager begins (BRK-S250). Its height, not its rect: a stuck pager's
 *  rect has moved. */
function contentEnd(page: Element): number {
  let box = page;
  while (box.childElementCount === 1 && box.firstElementChild) {
    box = box.firstElementChild;
  }
  const style = getComputedStyle(box);
  const dock = box.querySelector<HTMLElement>("[data-list-pager]");
  return (
    box.getBoundingClientRect().bottom -
    px(style.paddingBottom) -
    px(style.borderBottomWidth) -
    (dock?.offsetHeight ?? 0)
  );
}

interface CueButtonProps {
  shown: boolean;
  size: "md" | "sm";
  onClick: () => void;
  testId: string;
  className?: string;
}

function CueButton({
  shown,
  size,
  onClick,
  testId,
  className = "",
}: CueButtonProps) {
  return (
    <button
      type="button"
      tabIndex={-1}
      data-testid={testId}
      data-shown={shown}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`inline-flex items-center justify-center rounded-full bg-brk-panel text-brk-accent shadow-md shadow-brk-ink/15 ring-1 ring-brk-border transition-[opacity,transform,visibility] duration-200 hover:bg-brk-bg hover:text-brk-accent-strong motion-reduce:transition-none ${
        size === "md" ? "h-10 w-10" : "h-7 w-7"
      } ${TOUCH_SQUARE} ${
        shown
          ? "pointer-events-auto visible translate-y-0 opacity-100"
          : "pointer-events-none invisible translate-y-1 opacity-0"
      } ${className}`}
    >
      <ChevronDown size={size === "md" ? 20 : 16} strokeWidth={2} aria-hidden />
    </button>
  );
}

/** The page's cue. Render it as the last child of the scrolling column
 *  (`<main>`), straight after the page; `headerRef` is the sticky header, the
 *  top of the visible band a click scrolls by. */
export function PageScrollCue({
  headerRef,
}: {
  headerRef: RefObject<HTMLElement>;
}) {
  const line = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const lineEl = line.current;
    if (!lineEl) return;
    const update = () => {
      // Read on every check: a route change swaps the page element.
      const page = lineEl.previousElementSibling;
      setShown(
        page !== null &&
          !keepsOwnCue(page) &&
          hasMoreBelow(
            hiddenBelow(contentEnd(page), lineEl.getBoundingClientRect().top),
          ),
      );
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    // Content that grows or shrinks without a scroll (a fetch landing, a tab
    // switch, the BaseForm rail collapsing) moves the content's end, and the
    // column the cue ends resizes with it.
    const column = lineEl.parentElement;
    const unobserve = observeResize(column ? [column] : [], update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      unobserve();
    };
  }, []);

  function scrollOn() {
    const bottom =
      line.current?.getBoundingClientRect().top ?? window.innerHeight;
    const top = headerRef.current?.getBoundingClientRect().bottom ?? 0;
    window.scrollBy({
      top: scrollStep(top, bottom),
      behavior: scrollBehavior(),
    });
  }

  return (
    <div
      ref={line}
      data-scroll-cue="line"
      aria-hidden="true"
      className="pointer-events-none sticky bottom-[calc(1rem_+_var(--brk-dock-h)_+_env(safe-area-inset-bottom))] z-20 h-0 lg:bottom-[calc(var(--brk-footer-h)_+_var(--brk-dock-h)_+_1rem)]"
    >
      <CueButton
        testId="page-scroll-cue"
        size="md"
        shown={shown}
        onClick={scrollOn}
        className="absolute bottom-0 left-1/2 -translate-x-1/2"
      />
    </div>
  );
}

/** A scroll box's cue, overlaid on its bottom edge. Render it as a sibling of
 *  the box inside a `relative` wrapper; `watch` stands for the box's contents
 *  (e.g. a row count), since rows added or removed change its scroll height
 *  without resizing the box. */
export function ListScrollCue({
  targetRef,
  watch,
}: {
  targetRef: RefObject<HTMLElement>;
  watch?: unknown;
}) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const box = targetRef.current;
    if (!box) return;
    const update = () =>
      setShown(
        hasMoreBelow(
          hiddenBelow(box.scrollHeight - box.scrollTop, box.clientHeight),
          LIST_CUE_THRESHOLD_PX,
        ),
      );
    update();
    box.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    const unobserve = observeResize([box], update);
    return () => {
      box.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      unobserve();
    };
  }, [targetRef, watch]);

  function scrollOn() {
    const box = targetRef.current;
    if (!box) return;
    box.scrollBy({
      top: scrollStep(0, box.clientHeight),
      behavior: scrollBehavior(),
    });
  }

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-x-0 bottom-0 flex h-12 items-end justify-center bg-gradient-to-t from-brk-panel via-brk-panel/80 to-transparent pb-1 transition-opacity duration-200 motion-reduce:transition-none ${
        shown ? "opacity-100" : "opacity-0"
      }`}
    >
      <CueButton
        testId="list-scroll-cue"
        size="sm"
        shown={shown}
        onClick={scrollOn}
      />
    </div>
  );
}

/** A bottom dock's cue, in the dock itself (BRK-S359): the arrow between its
 *  buttons, and a fade over the content where it runs on under the dock's top
 *  edge, as the list's has. It shows while more than the page's threshold of
 *  `contentRef` is below the dock's top; at the page's end the dock rests
 *  after the content, so it goes. A click scrolls the page by 80% of the band
 *  between `topRef` (what stays put above the content, if anything) and the
 *  dock. Render it in the dock, a positioned element; `className` hides it
 *  where the dock has no room. `watch` stands for what the content is (the
 *  wizard's step). */
export function DockScrollCue({
  contentRef,
  dockRef,
  topRef,
  watch,
  className = "",
}: {
  contentRef: RefObject<HTMLElement>;
  dockRef: RefObject<HTMLElement>;
  topRef?: RefObject<HTMLElement>;
  watch?: unknown;
  className?: string;
}) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const content = contentRef.current;
    const dock = dockRef.current;
    if (!content || !dock) return;
    const update = () =>
      setShown(
        hasMoreBelow(
          hiddenBelow(
            content.getBoundingClientRect().bottom,
            dock.getBoundingClientRect().top,
          ),
        ),
      );
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    // A follow-up question opening grows the content without a scroll.
    const unobserve = observeResize([content], update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      unobserve();
    };
  }, [contentRef, dockRef, watch]);

  function scrollOn() {
    const bottom =
      dockRef.current?.getBoundingClientRect().top ?? window.innerHeight;
    const top = Math.max(
      0,
      topRef?.current?.getBoundingClientRect().bottom ?? 0,
    );
    window.scrollBy({
      top: scrollStep(top, bottom),
      behavior: scrollBehavior(),
    });
  }

  return (
    <span
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 ${className}`}
    >
      <span
        data-testid="dock-scroll-fade"
        className={`absolute inset-x-0 bottom-full h-6 bg-gradient-to-t from-brk-bg to-transparent transition-opacity duration-200 motion-reduce:transition-none ${
          shown ? "opacity-100" : "opacity-0"
        }`}
      />
      <button
        type="button"
        tabIndex={-1}
        data-testid="dock-scroll-cue"
        data-shown={shown}
        onMouseDown={(e) => e.preventDefault()}
        onClick={scrollOn}
        className={`absolute left-1/2 top-1/2 inline-flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-brk-border text-brk-dim transition-[opacity,visibility,color,border-color,background-color] duration-200 hover:border-brk-accent hover:bg-brk-accent/10 hover:text-brk-accent motion-reduce:transition-none ${TOUCH_SQUARE} ${
          shown
            ? "pointer-events-auto visible opacity-100"
            : "pointer-events-none invisible opacity-0"
        }`}
      >
        <ArrowDown size={16} strokeWidth={2} aria-hidden />
      </button>
    </span>
  );
}
